import React, { useState, useMemo, useCallback } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Icon,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import moment from 'moment';
import { apiPost } from './utils/apiService';

const { RangePicker } = DatePicker;
const { Option } = Select;

/*
 * Fuel Purchase Report
 * --------------------
 * Data source: POST /fuel/mileage-report (the same endpoint the Full Tank
 * Consumption Report uses). One call returns every fuel entry in the period
 * with asset metadata already populated; we filter and group entirely on the
 * client (fetch once, reuse — no per-asset calls).
 *
 * Business rule (confirmed with the business owner):
 *   - Each fuel entry carries a root-level boolean `isBowser`.
 *   - isBowser === true  -> fuel was ISSUED FROM A BOWSER (internal transfer).
 *     This is NOT a market purchase; the fuel was already purchased when the
 *     bowser itself was filled (that fill is its own isBowser === false entry).
 *     These entries are EXCLUDED from this report entirely to avoid
 *     double-counting — the count/litres excluded are surfaced in the summary
 *     (never silently dropped).
 *   - isBowser === false -> the fuel was BOUGHT FROM THE MARKET (a purchase);
 *     these are the entries the report shows.
 *   - "Total Fuel Purchased" = sum of quantity over the shown (non-bowser)
 *     entries. Bowser assets still appear when they are filled from the market
 *     (their own purchases), so all assets remain represented.
 *
 * Data reliability (see global AssetPro rules):
 *   - The endpoint deliberately returns entries OUTSIDE the requested window
 *     (it needs them for consumption maths). A purchase report must NOT count
 *     those, so we strictly re-filter by entry datetime on the client.
 *   - `isBowser` is matched exactly. When it is missing/undefined the entry
 *     goes to a flagged "Unknown source" bucket — it is never assumed to be a
 *     market purchase.
 *   - Entries with a missing/non-numeric quantity are excluded from totals and
 *     counted explicitly in the summary; they are never silently dropped and a
 *     missing value is never shown as zero.
 *   - Data is fetched fresh on each Fetch (fuel data is business-critical; no
 *     caching).
 */

const DEFAULT_RANGE = () => [moment().subtract(3, 'months').startOf('day'), moment().endOf('day')];

const UNSPECIFIED = 'Unspecified';

const SOURCE_MARKET = 'Market Purchase';
const SOURCE_BOWSER = 'Bowser Issue';
const SOURCE_UNKNOWN = 'Unknown';

// Time-bucket grouping is keyed on a sortable string; filter grouping keys on
// the dimension value itself.
const GROUP_OPTIONS = [
  { value: 'daily', label: 'Daily', kind: 'time' },
  { value: 'weekly', label: 'Weekly', kind: 'time' },
  { value: 'monthly', label: 'Monthly', kind: 'time' },
  { value: 'annually', label: 'Annually', kind: 'time' },
  { value: 'location', label: 'Location', kind: 'field', field: 'location' },
  { value: 'subLocation', label: 'Sub Location', kind: 'field', field: 'subLocation' },
  { value: 'assetType', label: 'Asset Type', kind: 'field', field: 'assetType' },
  { value: 'assetSubType', label: 'Asset Sub Type', kind: 'field', field: 'assetSubType' },
  { value: 'assetModel', label: 'Asset Model', kind: 'field', field: 'assetModel' },
  { value: 'assetCode', label: 'Asset Code', kind: 'field', field: 'assetCode' },
  { value: 'fuelType', label: 'Fuel Type', kind: 'field', field: 'fuelType' },
];

// Pull a display name out of a populated object / string reference.
const extractName = (field, fallback = '') => {
  if (!field) return fallback;
  if (typeof field === 'string') return field.trim() || fallback;
  if (typeof field === 'object') return field.name || field.code || field.title || field.altName || fallback;
  return fallback;
};

// Sub-location is stored as the "client" reference on a fuel entry; the system
// labels client = sub-location. Prefer subLocation, fall back to client.
const extractSubLocation = (entry) =>
  extractName(entry.subLocation, '') || extractName(entry.client, '') || UNSPECIFIED;

const uniqSorted = (arr) => [...new Set(arr.filter((v) => v && v !== UNSPECIFIED))].sort();

const fmtLitres = (v) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

// --- CSV helpers (same approach as SalesAssetReport.js) -----------------
const escapeCsv = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, header, rows) => {
  const lines = [header.map(escapeCsv).join(',')];
  rows.forEach((r) => lines.push(r.map(escapeCsv).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Map one raw fuel entry to a flat, normalized row. Returns null when the entry
// has no usable asset code (cannot be attributed).
const normalizeEntry = (entry, index) => {
  const assetCode =
    (entry.asset && typeof entry.asset === 'object' && entry.asset.assetCode) ||
    entry.assetCode ||
    '';
  const assetName =
    (entry.asset && typeof entry.asset === 'object' && (entry.asset.altName || entry.asset.description)) || '';
  const ownerCode =
    (entry.asset && typeof entry.asset === 'object' && entry.asset.ownerCode) || entry.ownerCode || '';

  const datetimeMs = entry.datetime
    ? Number(entry.datetime)
    : entry.date
    ? moment(entry.date).valueOf()
    : null;

  // Exact source classification — never assume market when the flag is absent.
  let source = SOURCE_UNKNOWN;
  if (entry.isBowser === true) source = SOURCE_BOWSER;
  else if (entry.isBowser === false) source = SOURCE_MARKET;

  const rawQty = entry.quantity;
  const qtyNum = Number(rawQty);
  const quantity = rawQty !== null && rawQty !== undefined && rawQty !== '' && Number.isFinite(qtyNum)
    ? qtyNum
    : null;

  return {
    key: entry._id || `row-${index}`,
    id: entry._id || '',
    datetimeMs,
    assetCode: assetCode || UNSPECIFIED,
    assetName,
    ownerCode,
    assetType: extractName(entry.assetType, UNSPECIFIED),
    assetSubType: extractName(entry.assetSubType, UNSPECIFIED),
    assetModel: extractName(entry.assetModel, UNSPECIFIED),
    location: extractName(entry.location, UNSPECIFIED),
    subLocation: extractSubLocation(entry),
    fuelType: extractName(entry.typeOfFuel, UNSPECIFIED),
    quantity,
    source,
    isMarket: source === SOURCE_MARKET,
    isBowserIssue: source === SOURCE_BOWSER,
    sourceKnown: source !== SOURCE_UNKNOWN,
    voucherNo: entry.voucherNo || entry.receiptNumber || '',
  };
};

const matchMulti = (selected, value) => !selected.length || selected.includes(value);

function FuelPurchaseReport() {
  const [range, setRange] = useState(DEFAULT_RANGE());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [rawRows, setRawRows] = useState([]); // normalized, strictly in-range
  const [queriedRange, setQueriedRange] = useState(null);
  const [reliability, setReliability] = useState({ missingQty: 0, unknownSource: 0, noAssetCode: 0, outOfRange: 0, bowserExcluded: 0, bowserExcludedL: 0 });

  // Multi-select filters (empty array = all)
  const [fLocation, setFLocation] = useState([]);
  const [fSubLocation, setFSubLocation] = useState([]);
  const [fAssetType, setFAssetType] = useState([]);
  const [fAssetSubType, setFAssetSubType] = useState([]);
  const [fAssetModel, setFAssetModel] = useState([]);
  const [fAssetCode, setFAssetCode] = useState([]);
  const [fFuelType, setFFuelType] = useState([]);

  const [groupBy, setGroupBy] = useState('monthly');

  const handleFetch = useCallback(async () => {
    if (!range || !range[0] || !range[1]) {
      message.warning('Pick a date range.');
      return;
    }
    const from = range[0].clone().startOf('day');
    const to = range[1].clone().endOf('day');
    const fromMs = from.valueOf();
    const toMs = to.valueOf();

    setLoading(true);
    setError(null);
    setRawRows([]);

    try {
      const res = await apiPost('/fuel/mileage-report', {
        asset: null,
        location: 'all',
        subLocation: 'all',
        periodFrom: from.toISOString(),
        periodTo: to.toISOString(),
      });

      if (!res || res.success === false) {
        throw new Error((res && res.error) || 'Failed to fetch fuel entries');
      }

      const entries = Array.isArray(res.data) ? res.data : [];

      let missingQty = 0;
      let unknownSource = 0;
      let noAssetCode = 0;
      let outOfRange = 0;
      let bowserExcluded = 0;
      let bowserExcludedL = 0;

      const rows = [];
      entries.forEach((entry, i) => {
        const row = normalizeEntry(entry, i);

        // Cannot attribute an entry with no asset code at all.
        const hasAsset =
          (entry.asset && typeof entry.asset === 'object' && entry.asset.assetCode) || entry.assetCode;
        if (!hasAsset) {
          noAssetCode += 1;
          return;
        }

        // Strict client-side date filter: the endpoint returns out-of-window
        // rows for consumption maths; a purchase report must not count them.
        if (row.datetimeMs === null || row.datetimeMs < fromMs || row.datetimeMs > toMs) {
          outOfRange += 1;
          return;
        }

        // Exclude bowser-issued fuel: this fuel was already PURCHASED from the
        // market when the bowser itself was filled (that fill appears as its
        // own market-purchase entry). Counting the bowser issue again would
        // double-count, so a purchase report only shows non-bowser entries.
        // The count/litres are surfaced in the summary — never silently dropped.
        if (row.isBowserIssue) {
          bowserExcluded += 1;
          if (row.quantity !== null) bowserExcludedL += row.quantity;
          return;
        }

        if (row.quantity === null) missingQty += 1;
        if (!row.sourceKnown) unknownSource += 1;

        rows.push(row);
      });

      setRawRows(rows);
      setReliability({ missingQty, unknownSource, noAssetCode, outOfRange, bowserExcluded, bowserExcludedL });
      setQueriedRange([from, to]);

      if (rows.length === 0) {
        message.info('No fuel entries found in this range.');
      }
    } catch (e) {
      console.error('[FuelPurchaseReport] fetch failed', e);
      setError(e.message || 'Failed to load fuel purchase report');
    } finally {
      setLoading(false);
    }
  }, [range]);

  // --- Cascading filter option lists (derived from raw, in-range rows) ----
  const locationOptions = useMemo(() => uniqSorted(rawRows.map((r) => r.location)), [rawRows]);
  const subLocationOptions = useMemo(
    () =>
      uniqSorted(
        rawRows.filter((r) => matchMulti(fLocation, r.location)).map((r) => r.subLocation)
      ),
    [rawRows, fLocation]
  );
  const assetTypeOptions = useMemo(() => uniqSorted(rawRows.map((r) => r.assetType)), [rawRows]);
  const assetSubTypeOptions = useMemo(
    () =>
      uniqSorted(
        rawRows.filter((r) => matchMulti(fAssetType, r.assetType)).map((r) => r.assetSubType)
      ),
    [rawRows, fAssetType]
  );
  const assetModelOptions = useMemo(
    () =>
      uniqSorted(
        rawRows
          .filter((r) => matchMulti(fAssetType, r.assetType))
          .filter((r) => matchMulti(fAssetSubType, r.assetSubType))
          .map((r) => r.assetModel)
      ),
    [rawRows, fAssetType, fAssetSubType]
  );
  const fuelTypeOptions = useMemo(() => uniqSorted(rawRows.map((r) => r.fuelType)), [rawRows]);
  // Asset choices: value is the asset code (the grouping/filter key), but each
  // option also carries Alt Name and Owner Code so the picker can be searched
  // by any of the three fields.
  const assetCodeChoices = useMemo(() => {
    const byCode = new Map();
    rawRows
      .filter((r) => matchMulti(fLocation, r.location))
      .filter((r) => matchMulti(fSubLocation, r.subLocation))
      .filter((r) => matchMulti(fAssetType, r.assetType))
      .filter((r) => matchMulti(fAssetSubType, r.assetSubType))
      .filter((r) => matchMulti(fAssetModel, r.assetModel))
      .filter((r) => matchMulti(fFuelType, r.fuelType))
      .forEach((r) => {
        if (!byCode.has(r.assetCode)) {
          byCode.set(r.assetCode, { code: r.assetCode, assetName: r.assetName, ownerCode: r.ownerCode });
        }
      });
    return Array.from(byCode.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [rawRows, fLocation, fSubLocation, fAssetType, fAssetSubType, fAssetModel, fFuelType]);

  // --- Apply filters ------------------------------------------------------
  const filteredRows = useMemo(
    () =>
      rawRows
        .filter((r) => matchMulti(fLocation, r.location))
        .filter((r) => matchMulti(fSubLocation, r.subLocation))
        .filter((r) => matchMulti(fAssetType, r.assetType))
        .filter((r) => matchMulti(fAssetSubType, r.assetSubType))
        .filter((r) => matchMulti(fAssetModel, r.assetModel))
        .filter((r) => matchMulti(fAssetCode, r.assetCode))
        .filter((r) => matchMulti(fFuelType, r.fuelType)),
    [rawRows, fLocation, fSubLocation, fAssetType, fAssetSubType, fAssetModel, fAssetCode, fFuelType]
  );

  // --- Summary metrics ----------------------------------------------------
  // rawRows already excludes bowser-issued entries, so every row here is a
  // market purchase (or a rare unknown-source entry that is flagged, not
  // assumed to be market).
  const summary = useMemo(() => {
    let marketL = 0;
    let unknownL = 0;
    let missingQty = 0;
    let unknownSource = 0;
    const assets = new Set();

    filteredRows.forEach((r) => {
      assets.add(r.assetCode);
      if (r.quantity === null) {
        missingQty += 1;
      } else if (r.isMarket) {
        marketL += r.quantity;
      } else {
        unknownL += r.quantity;
      }
      if (!r.sourceKnown) unknownSource += 1;
    });

    return {
      marketL,
      unknownL,
      entries: filteredRows.length,
      assets: assets.size,
      missingQty,
      unknownSource,
    };
  }, [filteredRows]);

  // --- Build group buckets ------------------------------------------------
  const groupConfig = useMemo(() => GROUP_OPTIONS.find((g) => g.value === groupBy), [groupBy]);

  const bucketOf = useCallback(
    (row) => {
      if (groupConfig.kind === 'field') {
        const v = row[groupConfig.field] || UNSPECIFIED;
        return { key: v, label: v };
      }
      const m = moment(row.datetimeMs);
      switch (groupBy) {
        case 'daily':
          return { key: m.format('YYYY-MM-DD'), label: m.format('DD MMM YYYY') };
        case 'weekly':
          return { key: m.format('GGGG-[W]WW'), label: m.format('[W]WW GGGG') };
        case 'monthly':
          return { key: m.format('YYYY-MM'), label: m.format('MMM YYYY') };
        case 'annually':
          return { key: m.format('YYYY'), label: m.format('YYYY') };
        default:
          return { key: UNSPECIFIED, label: UNSPECIFIED };
      }
    },
    [groupBy, groupConfig]
  );

  const groupedRows = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const { key, label } = bucketOf(r);
      if (!map.has(key)) {
        map.set(key, {
          key,
          label,
          rows: [],
          marketL: 0,
          unknownL: 0,
          assets: new Set(),
          missingQty: 0,
        });
      }
      const g = map.get(key);
      g.rows.push(r);
      g.assets.add(r.assetCode);
      if (r.quantity === null) g.missingQty += 1;
      else if (r.isMarket) g.marketL += r.quantity;
      else g.unknownL += r.quantity;
    });

    const list = Array.from(map.values()).map((g) => ({
      ...g,
      assetCount: g.assets.size,
      totalL: g.marketL + g.unknownL,
      entries: g.rows.length,
    }));

    // Time groups sort chronologically by key; field groups by total desc.
    if (groupConfig.kind === 'time') {
      list.sort((a, b) => String(a.key).localeCompare(String(b.key)));
    } else {
      list.sort((a, b) => b.totalL - a.totalL);
    }
    return list;
  }, [filteredRows, bucketOf, groupConfig]);

  // --- Detail columns (used for expanded rows and detail CSV) -------------
  const sourceTag = (s) => {
    if (s === SOURCE_MARKET) return <Tag color="green">{s}</Tag>;
    if (s === SOURCE_BOWSER) return <Tag color="blue">{s}</Tag>;
    return <Tag color="orange">Unknown source</Tag>;
  };

  const detailColumns = [
    {
      title: 'Date / Time',
      dataIndex: 'datetimeMs',
      key: 'datetimeMs',
      width: 150,
      sorter: (a, b) => (a.datetimeMs || 0) - (b.datetimeMs || 0),
      defaultSortOrder: 'descend',
      render: (v) => (v ? moment(v).format('DD MMM YYYY HH:mm') : '—'),
    },
    {
      title: 'Asset',
      dataIndex: 'assetCode',
      key: 'asset',
      width: 220,
      render: (_, r) => (
        <div style={{ lineHeight: 1.3 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.assetCode}</span>
          {(r.assetName || r.ownerCode) && (
            <div style={{ fontSize: 12, color: '#888' }}>
              {[r.assetName, r.ownerCode && `Owner: ${r.ownerCode}`].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      ),
    },
    { title: 'Type', dataIndex: 'assetType', key: 'assetType', width: 120 },
    { title: 'Sub Type', dataIndex: 'assetSubType', key: 'assetSubType', width: 130 },
    { title: 'Model', dataIndex: 'assetModel', key: 'assetModel', width: 160, ellipsis: true },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 130 },
    { title: 'Sub Location', dataIndex: 'subLocation', key: 'subLocation', width: 130 },
    { title: 'Fuel Type', dataIndex: 'fuelType', key: 'fuelType', width: 100, render: (v) => v || '—' },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 140,
      render: (v) => sourceTag(v),
    },
    {
      title: 'Quantity (L)',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.quantity || 0) - (b.quantity || 0),
      render: (v) => (v === null ? <Tag color="red">Missing</Tag> : fmtLitres(v)),
    },
    { title: 'Voucher', dataIndex: 'voucherNo', key: 'voucherNo', width: 110, render: (v) => v || '—' },
  ];

  const groupColumns = [
    {
      title: groupConfig ? groupConfig.label : 'Group',
      dataIndex: 'label',
      key: 'label',
      width: 260,
      render: (v) => <strong>{v}</strong>,
    },
    { title: 'Entries', dataIndex: 'entries', key: 'entries', width: 90, align: 'right' },
    { title: 'Assets', dataIndex: 'assetCount', key: 'assetCount', width: 90, align: 'right' },
    {
      title: 'Fuel Purchased (L)',
      dataIndex: 'marketL',
      key: 'marketL',
      width: 170,
      align: 'right',
      sorter: (a, b) => a.marketL - b.marketL,
      render: (v) => <strong style={{ color: '#389e0d' }}>{fmtLitres(v)}</strong>,
    },
  ];

  // --- CSV exports --------------------------------------------------------
  const stamp = () => (queriedRange ? `${queriedRange[0].format('YYYYMMDD')}_${queriedRange[1].format('YYYYMMDD')}` : moment().format('YYYYMMDD'));

  const handleExportDetail = useCallback(() => {
    if (!filteredRows.length) {
      message.warning('Nothing to export.');
      return;
    }
    const header = [
      'Date/Time', 'Asset Code', 'Alt Name', 'Owner Code', 'Asset Type', 'Asset Sub Type', 'Asset Model',
      'Location', 'Sub Location', 'Fuel Type', 'Source', 'Quantity (L)', 'Voucher No',
    ];
    const rows = filteredRows.map((r) => [
      r.datetimeMs ? moment(r.datetimeMs).format('YYYY-MM-DD HH:mm') : '',
      r.assetCode,
      r.assetName,
      r.ownerCode,
      r.assetType,
      r.assetSubType,
      r.assetModel,
      r.location,
      r.subLocation,
      r.fuelType,
      r.source === SOURCE_UNKNOWN ? 'Unknown source' : r.source,
      r.quantity === null ? '' : r.quantity,
      r.voucherNo,
    ]);
    downloadCsv(`fuel-purchase-detail-${stamp()}.csv`, header, rows);
  }, [filteredRows, queriedRange]);

  const handleExportGrouped = useCallback(() => {
    if (!groupedRows.length) {
      message.warning('Nothing to export.');
      return;
    }
    const header = [
      `${groupConfig.label}`, 'Entries', 'Assets', 'Fuel Purchased (L)', 'Unknown Source (L)',
    ];
    const rows = groupedRows.map((g) => [
      g.label, g.entries, g.assetCount,
      g.marketL.toFixed(2), g.unknownL.toFixed(2),
    ]);
    downloadCsv(`fuel-purchase-grouped-${groupBy}-${stamp()}.csv`, header, rows);
  }, [groupedRows, groupConfig, groupBy, queriedRange]);

  const resetFilters = () => {
    setFLocation([]);
    setFSubLocation([]);
    setFAssetType([]);
    setFAssetSubType([]);
    setFAssetModel([]);
    setFAssetCode([]);
    setFFuelType([]);
  };

  const renderMultiSelect = (placeholder, value, onChange, options) => (
    <Select
      mode="multiple"
      allowClear
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      style={{ width: '100%' }}
      maxTagCount={2}
      showSearch
      optionFilterProp="children"
      filterOption={(input, opt) => String(opt.props.children).toLowerCase().includes(input.toLowerCase())}
    >
      {options.map((o) => (
        <Option key={o} value={o}>{o}</Option>
      ))}
    </Select>
  );

  // Asset picker: value = asset code, but each option shows and is searchable
  // by Asset Code, Alt Name and Owner Code.
  const renderAssetSelect = () => (
    <Select
      mode="multiple"
      allowClear
      placeholder="Asset — search code / name / owner (all)"
      value={fAssetCode}
      onChange={setFAssetCode}
      style={{ width: '100%' }}
      maxTagCount={2}
      showSearch
      optionLabelProp="label"
      filterOption={(input, opt) => String(opt.props.title || '').toLowerCase().includes(input.toLowerCase())}
    >
      {assetCodeChoices.map((c) => (
        <Option
          key={c.code}
          value={c.code}
          label={c.code}
          title={`${c.code} ${c.assetName || ''} ${c.ownerCode || ''}`}
        >
          <div style={{ lineHeight: 1.3 }}>
            <span style={{ fontFamily: 'monospace' }}>{c.code}</span>
            {(c.assetName || c.ownerCode) && (
              <div style={{ fontSize: 12, color: '#888' }}>
                {[c.assetName, c.ownerCode && `Owner: ${c.ownerCode}`].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </Option>
      ))}
    </Select>
  );

  const hasData = rawRows.length > 0;
  const activeFilterSummary = (label, arr) => `${label}: ${arr.length ? arr.join(', ') : 'All'}`;

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={<span><Icon type="shopping-cart" /> Fuel Purchase Report</span>}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[12, 12]} type="flex" align="middle">
          <Col>
            <span style={{ marginRight: 8, color: '#666' }}>Date range:</span>
            <RangePicker
              value={range}
              onChange={(v) => setRange(v)}
              format="YYYY-MM-DD"
              allowClear={false}
              ranges={{
                Today: [moment().startOf('day'), moment().endOf('day')],
                'Last 7 Days': [moment().subtract(7, 'days').startOf('day'), moment().endOf('day')],
                'Last 30 Days': [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')],
                'This Month': [moment().startOf('month'), moment().endOf('month')],
                'Last 3 Months': [moment().subtract(3, 'months').startOf('day'), moment().endOf('day')],
                'This FY': [moment().month(3).startOf('month'), moment().endOf('day')],
              }}
            />
          </Col>
          <Col>
            <Button type="primary" icon="search" loading={loading} onClick={handleFetch}>Fetch</Button>
          </Col>
          <Col>
            <Button icon="download" disabled={!filteredRows.length} onClick={handleExportDetail}>Export Detail CSV</Button>
          </Col>
          <Col>
            <Button icon="download" disabled={!groupedRows.length} onClick={handleExportGrouped}>Export Grouped CSV</Button>
          </Col>
        </Row>

        {hasData && (
          <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6}>{renderMultiSelect('Location (all)', fLocation, setFLocation, locationOptions)}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>{renderMultiSelect('Sub Location (all)', fSubLocation, setFSubLocation, subLocationOptions)}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>{renderMultiSelect('Asset Type (all)', fAssetType, setFAssetType, assetTypeOptions)}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>{renderMultiSelect('Asset Sub Type (all)', fAssetSubType, setFAssetSubType, assetSubTypeOptions)}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>{renderMultiSelect('Asset Model (all)', fAssetModel, setFAssetModel, assetModelOptions)}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>{renderAssetSelect()}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>{renderMultiSelect('Fuel Type (all)', fFuelType, setFFuelType, fuelTypeOptions)}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <span style={{ marginRight: 8, color: '#666' }}>Group by:</span>
              <Select value={groupBy} onChange={setGroupBy} style={{ width: 150 }}>
                {GROUP_OPTIONS.map((g) => <Option key={g.value} value={g.value}>{g.label}</Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button icon="reload" onClick={resetFilters}>Reset Filters</Button>
            </Col>
          </Row>
        )}

        {error && (
          <Alert
            type="error"
            showIcon
            message="Failed to load fuel purchase report"
            description={error}
            style={{ marginTop: 12 }}
            closable
            onClose={() => setError(null)}
          />
        )}
      </Card>

      {hasData && (
        <Card title="Query Summary" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
            <div>
              <strong>Period:</strong>{' '}
              {queriedRange ? `${queriedRange[0].format('DD MMM YYYY')} – ${queriedRange[1].format('DD MMM YYYY')}` : '—'}
            </div>
            <div>{activeFilterSummary('Location', fLocation)} &nbsp;|&nbsp; {activeFilterSummary('Sub Location', fSubLocation)}</div>
            <div>{activeFilterSummary('Asset Type', fAssetType)} &nbsp;|&nbsp; {activeFilterSummary('Asset Sub Type', fAssetSubType)}</div>
            <div>{activeFilterSummary('Asset Model', fAssetModel)} &nbsp;|&nbsp; {activeFilterSummary('Asset Code', fAssetCode)}</div>
            <div>{activeFilterSummary('Fuel Type', fFuelType)}</div>
          </div>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="Total Fuel Purchased (market)"
                value={summary.marketL}
                precision={2}
                suffix="L"
                valueStyle={{ color: '#389e0d' }}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic title="Fuel Entries" value={summary.entries} />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic title="Assets" value={summary.assets} />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="Bowser Issues Excluded"
                value={reliability.bowserExcluded}
                suffix={reliability.bowserExcludedL ? `(${fmtLitres(reliability.bowserExcludedL)} L)` : ''}
                valueStyle={{ color: '#8c8c8c' }}
              />
            </Col>
          </Row>

          {reliability.bowserExcluded > 0 && (
            <Alert
              style={{ marginTop: 16 }}
              type="info"
              showIcon
              message={`${reliability.bowserExcluded} bowser-issued fuel entr${reliability.bowserExcluded === 1 ? 'y' : 'ies'} (${fmtLitres(reliability.bowserExcludedL)} L) excluded from this report`}
              description="Fuel issued from a bowser is not a market purchase — it was already purchased from the market when the bowser itself was filled (that fill appears as its own purchase entry). It is excluded here to avoid double-counting."
            />
          )}

          {(summary.missingQty > 0 || summary.unknownSource > 0 || summary.unknownL > 0 || reliability.outOfRange > 0 || reliability.noAssetCode > 0) && (
            <Alert
              style={{ marginTop: 16 }}
              type="warning"
              showIcon
              message="Data reliability notes"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                  {summary.missingQty > 0 && (
                    <li>{summary.missingQty} entr{summary.missingQty === 1 ? 'y has' : 'ies have'} a missing/non-numeric quantity — excluded from all litre totals (not counted as zero).</li>
                  )}
                  {summary.unknownSource > 0 && (
                    <li>{summary.unknownSource} entr{summary.unknownSource === 1 ? 'y is' : 'ies are'} missing the bowser flag — classified as "Unknown source" and excluded from the market purchase total ({fmtLitres(summary.unknownL)} L), never assumed to be a market purchase.</li>
                  )}
                  {reliability.outOfRange > 0 && (
                    <li>{reliability.outOfRange} entr{reliability.outOfRange === 1 ? 'y' : 'ies'} returned by the API fell outside the selected date range and {reliability.outOfRange === 1 ? 'was' : 'were'} excluded.</li>
                  )}
                  {reliability.noAssetCode > 0 && (
                    <li>{reliability.noAssetCode} entr{reliability.noAssetCode === 1 ? 'y' : 'ies'} had no asset code and could not be attributed — excluded.</li>
                  )}
                </ul>
              }
            />
          )}
        </Card>
      )}

      <Card
        title={
          <span>
            {groupConfig ? `Grouped by ${groupConfig.label}` : 'Results'}{' '}
            <Tag style={{ marginLeft: 8 }}>{groupedRows.length} group{groupedRows.length === 1 ? '' : 's'}</Tag>
            <span style={{ color: '#999', fontWeight: 'normal', marginLeft: 8 }}>
              {filteredRows.length} entr{filteredRows.length === 1 ? 'y' : 'ies'}
            </span>
          </span>
        }
      >
        <Table
          columns={groupColumns}
          dataSource={groupedRows}
          loading={loading}
          size="middle"
          scroll={{ x: 620 }}
          pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'] }}
          expandedRowRender={(group) => (
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Table
                columns={detailColumns}
                dataSource={group.rows}
                size="small"
                rowKey="key"
                pagination={group.rows.length > 20 ? { pageSize: 20 } : false}
                scroll={{ x: 1500 }}
              />
            </div>
          )}
        />
      </Card>
    </div>
  );
}

export default FuelPurchaseReport;
